import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, History, MailCheck, MailWarning, Repeat, Send, UsersRound, X } from "lucide-react";
import { AssignmentPacketPanel } from "@/components/assignments/AssignmentPacketPanel";
import { DrawerControls } from "@/components/board/DrawerControls";
import { EditorModal } from "@/components/EditorModal";
import { Dash, PriorityCell, StatusPill, ageLabel, formatDate, refillLabel } from "@/components/board/boardFormat";
import { api, formatApiError } from "@/lib/api";

const FILL_EVENT_LABEL = { filled: "Filled", vacated: "Vacated", reopened: "Reopened" };

const Field = ({ label, value, testId }) => (
  <div className="drawer-field">
    <small>{label}</small>
    <span data-testid={testId}>{value || <Dash />}</span>
  </div>
);

const ConfirmationState = ({ item }) => {
  if (!item.provider_id) return <small className="drawer-confirmation-state">No provider portal account</small>;
  if (!item.confirmation) return <small className="drawer-confirmation-state is-missing" data-testid={`confirmation-state-${item.id}`}>Confirmation not sent</small>;
  const { sent_at, sent_by_name, email_sent, sent_count } = item.confirmation;
  return (
    <small className={`drawer-confirmation-state ${email_sent ? "is-sent" : "is-warn"}`} data-testid={`confirmation-state-${item.id}`}>
      {email_sent ? <MailCheck size={12} /> : <MailWarning size={12} />}
      Confirmation {sent_count > 1 ? `sent ×${sent_count}` : "sent"} {formatDate(sent_at, true)} by {sent_by_name}
      {!email_sent && " · email not delivered"}
    </small>
  );
};

export const RequestDrawer = ({ canManage, candidates, focus, meta, providers, requestId, onChanged, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/staffing-board/${requestId}/detail`);
      setDetail(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [requestId]);

  useEffect(() => {
    setDetail(null);
    setMessage("");
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  async function handleChanged(note) {
    setMessage(note);
    await load();
    onChanged();
  }

  const request = detail?.request;
  return createPortal(
    <div className="drawer-backdrop" data-testid="request-drawer-backdrop" onClick={onClose}>
      <aside aria-modal="true" className="request-drawer" data-testid="request-drawer" role="dialog" onClick={(event) => event.stopPropagation()}>
        <header className="drawer-header">
          <div>
            <p className="drawer-id" data-testid="drawer-request-id">{requestId}</p>
            <h2 data-testid="drawer-role">{request?.role_name || "Loading…"}</h2>
            {request && (
              <div className="drawer-pills">
                <StatusPill status={request.status} testId="drawer-status" />
                <PriorityCell priority={request.priority} testId="drawer-priority" />
              </div>
            )}
          </div>
          <button aria-label="Close details" className="drawer-close" data-testid="close-request-drawer" type="button" onClick={onClose}><X size={18} /></button>
        </header>
        <div className="drawer-body">
          {error && <p className="form-message error-message" data-testid="drawer-error">{error}</p>}
          {message && <p className="form-message success-message" data-testid="drawer-message">{message}</p>}
          {request && (
            <>
              {request.attention_reasons.length > 0 && (
                <div className="drawer-attention" data-testid="drawer-attention">
                  <AlertTriangle size={15} />
                  <span>Needs attention: {request.attention_reasons.join(" · ")}</span>
                </div>
              )}
              <section className="drawer-section">
                <h3>Request details</h3>
                <div className="drawer-field-grid">
                  <Field label="Client" value={request.client_id ? request.client_name : ""} testId="drawer-client" />
                  <Field label="Region" value={request.region} testId="drawer-region" />
                  <Field label="Date needed" value={request.needed_date && formatDate(request.needed_date)} testId="drawer-needed" />
                  <Field label="Weekly hours" value={request.hours ? `${request.hours} hrs` : ""} testId="drawer-hours" />
                  <Field label="Employment" value={request.employment_type} testId="drawer-employment" />
                  <Field label="Assignment type" value={request.engagement_type} testId="drawer-engagement" />
                  <Field label="Recruiter" value={request.assigned_recruiter} testId="drawer-recruiter" />
                  <Field label="Candidate" value={request.assigned_candidate} testId="drawer-candidate" />
                  <Field label="Last updated" value={formatDate(request.last_updated, true)} testId="drawer-updated" />
                  <Field label={request.original_request_date_source === "needed_date" ? "Original request date (from date needed)" : "Original request date"} value={request.original_request_date && formatDate(request.original_request_date, request.original_request_date_source === "created")} testId="drawer-created" />
                  <Field label={request.age_since === "reopened" ? "Age (since reopening)" : "Age (since request)"} value={request.age_days !== null && request.age_days !== undefined ? `${ageLabel(request)}${request.age_since === "reopened" ? ` · reopened ${formatDate(request.reopened_at)}` : ""}` : ""} testId="drawer-age" />
                  {request.reopen_count > 0 && <Field label="Time to refill" value={refillLabel(request)} testId="drawer-time-to-refill" />}
                </div>
                {request.required_credentials.length > 0 && (
                  <div className="drawer-credentials" data-testid="drawer-credentials">
                    {request.required_credentials.map((item) => <span key={item}>{item}</span>)}
                  </div>
                )}
                {request.notes && <p className="drawer-notes" data-testid="drawer-notes">{request.notes}</p>}
              </section>
              {canManage && (
                <section className="drawer-section">
                  <h3>Assignment controls</h3>
                  <DrawerControls key={request.last_updated} candidates={candidates} focus={focus} meta={meta} providers={providers} request={request} onChanged={handleChanged} />
                </section>
              )}
              <section className="drawer-section">
                <h3><Repeat size={14} /> Fill history</h3>
                {request.fill_history?.length ? (
                  <ol className="drawer-timeline fill-history" data-testid="drawer-fill-history">
                    {request.fill_history.map((event) => (
                      <li key={event.id} className={`fill-event fill-event-${event.kind}`} data-testid={`fill-event-${event.id}`}>
                        <strong>{FILL_EVENT_LABEL[event.kind] || event.kind}{event.person_name ? ` · ${event.person_name}` : ""}</strong>
                        {event.kind === "vacated" && event.reason && <p>Reason: {event.reason}</p>}
                        {event.detail && <p>{event.detail}</p>}
                        <small>{event.actor_name || "System"} · {formatDate(event.at, true)}</small>
                      </li>
                    ))}
                  </ol>
                ) : <p className="drawer-empty" data-testid="drawer-fill-history-empty">Not filled yet — age counts from the original request date.</p>}
              </section>
              <section className="drawer-section">
                <h3><UsersRound size={14} /> Assignments</h3>
                {detail.assignments.length ? (
                  <ul className="drawer-list drawer-assignment-list" data-testid="drawer-assignments">
                    {detail.assignments.map((item) => (
                      <li key={item.id} data-testid={`drawer-assignment-${item.id}`}>
                        <div className="drawer-assignment-main">
                          <strong>{item.person_name || "Unnamed"}</strong>
                          <span>{item.status}{item.start_date ? ` · ${formatDate(item.start_date)}` : ""}{item.end_date ? ` → ${formatDate(item.end_date)}` : ""}</span>
                        </div>
                        <div className="drawer-assignment-confirmation">
                          <ConfirmationState item={item} />
                          {canManage && item.provider_id && (
                            <button className="button button-secondary drawer-confirmation-button" data-testid={`send-confirmation-${item.id}`} type="button" onClick={() => setConfirming(item.id)}>
                              <Send size={13} /> {item.confirmation ? "Assignment email" : "Assignment email"}
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : <p className="drawer-empty" data-testid="drawer-assignments-empty">No assignment recorded yet.</p>}
              </section>
              <section className="drawer-section">
                <h3><History size={14} /> Activity</h3>
                {detail.activity.length ? (
                  <ol className="drawer-timeline" data-testid="drawer-activity">
                    {detail.activity.map((item) => (
                      <li key={item.id}>
                        <strong>{item.action}</strong>
                        {item.detail && <p>{item.detail}</p>}
                        <small>{item.actor_name || "System"} · {formatDate(item.occurred_at, true)}</small>
                      </li>
                    ))}
                  </ol>
                ) : <p className="drawer-empty" data-testid="drawer-activity-empty">No activity has been recorded for this request.</p>}
              </section>
            </>
          )}
        </div>
      </aside>
      {confirming && (
        <EditorModal onClose={() => setConfirming(null)} testId="assignment-confirmation-modal">
          <AssignmentPacketPanel
            assignmentId={confirming}
            compact
            onClose={() => setConfirming(null)}
            onSent={(result) => {
              setConfirming(null);
              handleChanged(
                result.packet.email_sent
                  ? `Assignment email sent to ${result.provider.name} (${result.provider.email}).`
                  : `Assignment posted to ${result.provider.name}'s portal · email not delivered (${result.packet.email_error}).`,
              );
            }}
          />
        </EditorModal>
      )}
    </div>,
    document.body,
  );
};
